import React from 'react';
import { C, CARD, H2, SUB, StatusChip } from './ui';
import { companyOrder, companyName, plOf, vendorOf } from './wizardLogic';

// Estado por empresa de los 4 bloques. Sirve como último paso y como panel
// de "Status" en cualquier momento (p. ej. tras perder la conexión con Sage).
function rowsFor(draft, id) {
    const c = draft.companies[id];
    const isMain = Number(id) === Number(draft.mainCompany);
    const p = draft.product;

    const entityRow = (label, entity, value, sageWhere) => {
        if (value) {
            return { label, status: 'done', detail: entity.mode === 'existing' ? `${value} (existing)` : `${value} (created)` };
        }
        if (entity.mode === 'new' && entity.task.status !== 'idle') {
            return { label, status: entity.task.status, detail: entity.form.code, todo: todoFor(entity.task, `${label} ${entity.form.code}`, sageWhere) };
        }
        return { label, status: 'idle', todo: `Choose or create the ${label.toLowerCase()}.` };
    };

    const pl = plOf(c);
    const vendor = vendorOf(c);
    const rows = [
        entityRow('Product Line', c.productLine, pl ? `${pl.code} — ${pl.desc}` : '', 'Inventory Management → Product Line Maintenance'),
        entityRow('Vendor', c.vendor, vendor ? `${vendor.code} — ${vendor.name}` : '', 'Accounts Payable → Vendor Maintenance'),
    ];

    const t = c.item.task;
    let itemStatus = t.status;
    let itemTodo = todoFor(t, `item ${p.form.itemCode}`, 'Common Information → Item Maintenance');
    if (t.status === 'done' && isMain) {
        if (p.portalTask.status !== 'done') { itemStatus = p.portalTask.status === 'idle' ? 'unconfirmed' : p.portalTask.status; itemTodo = 'In Sage, but the portal record is missing — open the item step and retry.'; }
        else if (p.imageName && p.imageTask.status !== 'done') { itemStatus = 'unconfirmed'; itemTodo = 'Image not uploaded — retry from the item step or upload it later.'; }
    }
    if (t.status === 'idle') itemTodo = 'Create the item in Sage.';
    rows.push({ label: 'Item', status: itemStatus, detail: p.form.itemCode || '', todo: itemStatus === 'done' ? '' : itemTodo });

    const pr = c.prices;
    if (pr.skipped) {
        const costNote = pr.costTask.status === 'done' ? 'Cost is in Sage, but the portal prices are missing' : 'No cost or prices yet';
        rows.push({ label: 'Prices & Cost', status: 'skipped', todo: `${costNote}. The item stays inactive in the catalog until it has a cost and a sale price (Price Management).` });
    } else if (pr.costTask.status === 'done' && pr.portalTask.status === 'done') {
        rows.push({ label: 'Prices & Cost', status: 'done', detail: `Cost $${Number(pr.form.cost).toFixed(2)} · L1 $${Number(pr.form.level1Price).toFixed(2)}` });
    } else if (pr.costTask.status !== 'done') {
        rows.push({ label: 'Prices & Cost', status: pr.costTask.status, todo: pr.costTask.status === 'idle' ? 'Enter the cost and prices, or skip.' : todoFor(pr.costTask, 'the cost', 'Item Maintenance → Standard Unit Cost') });
    } else {
        rows.push({ label: 'Prices & Cost', status: pr.portalTask.status === 'idle' ? 'unconfirmed' : pr.portalTask.status, todo: 'Cost is in Sage; the portal prices are missing — retry or set them in Price Management.' });
    }
    return rows;
}

function todoFor(task, what, sageWhere) {
    if (task.status === 'done') return '';
    if (task.status === 'unconfirmed') return `Sent but not confirmed. Check ${what} in Sage (${sageWhere}) before resending — Retry does that check for you.`;
    if (task.status === 'conflict') return `${what} already exists in Sage with different data. Review it in ${sageWhere}.`;
    if (task.status === 'failed') return task.sent ? `Retry ${what}; Sage will be checked first.` : `Nothing reached Sage. Retry ${what}.`;
    return '';
}

export function companyIsComplete(draft, id) {
    return rowsFor(draft, id).every((r) => r.status === 'done');
}

export default function SummaryView({ draft, compact }) {
    return (
        <div style={compact ? {} : CARD}>
            {!compact && (
                <>
                    <h2 style={H2}>Summary</h2>
                    <p style={SUB}>{draft.product.form.itemCode ? <>Item <b>{draft.product.form.itemCode}</b> — {draft.product.form.description}</> : 'No item yet'}</p>
                </>
            )}
            {companyOrder(draft).map((id, i) => (
                <div key={id} style={{ border: `1px solid ${C.line}`, borderRadius: '10px', padding: '0.75rem 1rem', marginTop: '0.75rem', background: '#FFFFFF' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <div style={{ fontWeight: 800, color: C.ink }}>{companyName(id)} <span style={{ fontWeight: 500, color: C.faint, fontSize: '0.8rem' }}>· {i === 0 ? 'Main' : 'Additional'}</span></div>
                        {companyIsComplete(draft, id) ? <StatusChip status="done" /> : <span style={{ fontSize: '0.75rem', fontWeight: 700, color: C.warn }}>Pending steps</span>}
                    </div>
                    {rowsFor(draft, id).map((r) => (
                        <div key={r.label} style={{ borderTop: `1px solid ${C.line}`, marginTop: '0.6rem', paddingTop: '0.6rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: 700, fontSize: '0.875rem', color: C.text }}>{r.label}</div>
                                    {r.detail && <div style={{ fontSize: '0.8rem', color: C.faint, wordBreak: 'break-word' }}>{r.detail}</div>}
                                </div>
                                <StatusChip status={r.status} />
                            </div>
                            {r.todo && <div style={{ fontSize: '0.8rem', color: r.status === 'failed' || r.status === 'conflict' ? C.err : C.warn, marginTop: '0.3rem' }}>→ {r.todo}</div>}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}
